import $ from 'jquery'
import 'autocomplete.js/dist/autocomplete.jquery'
import { trackEvent } from './analytics'
import autocomplete from 'autocomplete.js'
import cn from 'classnames'
import dedent from 'dedent'

/**
 * Autocomplete Component
 *
 * This is a wrapper around Autocomplete.js by the fine folks at Algolia.
 * https://github.com/algolia/autocomplete.js
 */
export const Autocomplete = {
  resultsPerPage: 10,

  /**
   * Set up any autocomplete inputs on the page.
   *
   * @return {void}
   */
  init: function () {
    $('.js-autocomplete').each(function () {
      Autocomplete.setupAutocomplete(
        this,
        $(this).data('endpoint'),
        $(this).data('display-key')
      )
    })
  },

  /**
   * Set up an autocomplete input.
   *
   * @param  {HTMLElement} input      The autocomplete input.
   * @param  {String}      endpoint   The JSON endpoint to pull results from.
   * @param  {String}      displayKey The property that should be displayed in the dropdown.
   * @return {void}
   */
  setupAutocomplete: function (input, endpoint, displayKey) {
    $(input)
      .autocomplete(
        {
          autoselect: true,
          hint: false,
          cssClasses: {
            root: 'autocomplete',
            prefix: 'autocomplete',
          },
        },
        [
          {
            source: function (query, callback) {
              Autocomplete.search(input, endpoint, query, callback)
            },
            displayKey,
            debounce: $(input).data('debounce') || 250,
            templates: {
              empty: '<div class="autocomplete-none">No results found</div>',
              suggestion: function (suggestion) {
                return suggestionResults(
                  $(input).data('display-template'),
                  displayKey,
                  suggestion
                )
              },
              header: function (query, isEmpty) {
                const resultsPerPage =
                  $(input).attr('data-per-page') || Autocomplete.resultsPerPage

                if (
                  !isEmpty &&
                  this.cachedSuggestions.length >= resultsPerPage
                ) {
                  return (
                    '<div class="autocomplete-header">Top ' +
                    resultsPerPage +
                    ' results</div>'
                  )
                }
              },
            },
          },
        ]
      )
      .on('autocomplete:opened', function () {
        $(input).addClass('autocomplete-input--open')
      })
      .on('autocomplete:closed', function () {
        $(input).removeClass('autocomplete-input--open')
      })
  },

  /**
   * Request data for the dropdown.
   *
   * @param  {HTMLElement}   input      The autocomplete element.
   * @param  {String}        endpoint   The JSON endpoint to query.
   * @param  {String}        query      The search query.
   * @param  {Function}      callback   Callback for processing the results.
   * @return {void}
   */
  search: function (input, endpoint, query, callback) {
    // Show loading state.
    $(input).parent().addClass('u-is-loading-input')

    const perPage =
      $(input).attr('data-per-page') || Autocomplete.resultsPerPage

    trackEvent('Autocomplete', 'search', endpoint)

    // Request the data.
    $.getJSON(endpoint, { query: query, per_page: perPage }, function (data) {
      // Cancel loading state.
      $(input).parent().removeClass('u-is-loading-input')
      callback(data)
    })
  },
}

/**
 * Create HTML template for suggestion item
 * @param  {String}  name        The name of the template to use.
 * @param  {String}  displayKey  The key to use as the main display label.
 * @param  {Object}  suggestion  Suggestion data.
 * @return {String}
 */
function suggestionResults(name, displayKey, suggestion) {
  switch (name) {
    case 'server':
      return serverResults(displayKey, suggestion)
    case 'server-pinning':
      return serverResults(displayKey, suggestion, true)
    case 'template':
      return templateResults(displayKey, suggestion)
    case 'message-stream':
      return messageStreamResults(displayKey, suggestion)
    default:
      return dedent`
        <div class="aa-suggestion u-textalign-left">
          ${autocomplete.escapeHighlightedString(suggestion[displayKey])}
        </div>`
  }
}

/**
 * Generate server suggestion template
 * @param  {String}  displayKey  The key to use as the main display label.
 * @param  {Object}  suggestion  Suggestion data.
 * @param  {Boolean} pinning     Whether users can pin the server.
 * @return {String}
 */
function serverResults(displayKey, suggestion, pinning) {
  let contents =
    '<span class="server-suggestion_color label-' +
    suggestion.color +
    '"></span>' +
    '<span class="server-suggestion_label">' +
    autocomplete.escapeHighlightedString(suggestion[displayKey]) +
    '</span>'

  if (pinning) {
    const pinLabel = (suggestion.favorite ? 'Unpin' : 'Pin') + ' this server'
    contents +=
      '<a href="' +
      suggestion.favorite_url +
      '" class="server_pin server-suggestion_pin js-pin-server js-tooltip ' +
      (suggestion.favorite ? 'is-active' : '') +
      '" data-tooltip="' +
      pinLabel +
      '" data-method="post"></a>'
  }

  return '<div class="aa-suggestion server-suggestion">' + contents + '</div>'
}

/**
 * Generate template suggestion
 * @param  {String}  displayKey  The key to use as the main display label.
 * @param  {Object}  suggestion  Suggestion data.
 * @return {String}
 */
function templateResults(displayKey, suggestion) {
  const templateType = suggestion.template_type
  const className = cn('template-suggestion_type', {
    'template-suggestion_type--layout': templateType === 'layout',
    'template-suggestion_type--template': templateType === 'standard',
  })

  const contents = autocomplete.escapeHighlightedString(suggestion[displayKey])

  return dedent`
    <div class="aa-suggestion template-suggestion u-textalign-left">
      ${contents}
      <span class="${className}">${templateType}</span>
    </div>`
}

/**
 * Generate message stream suggestion template
 * @param  {String}  displayKey  The key to use as the main display label.
 * @param  {Object}  suggestion  Suggestion data.
 * @return {String}
 */
function messageStreamResults(displayKey, suggestion) {
  return dedent`
      <div class="aa-suggestion message-stream-suggestion">
        ${autocomplete.escapeHighlightedString(suggestion[displayKey])}
      </div>
    `
}

$(Autocomplete.init)
